<script setup>
import { onMounted, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

const store = useStore();
const { t } = useI18n();
const wholesale = useMapGetter('reseller/getWholesale');

const isEnabled = computed(() => wholesale.value?.wholesale_enabled);
const current = computed(() => wholesale.value?.current || {});
const invoices = computed(() => wholesale.value?.invoices || []);

const formatMoney = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));

const formatDate = value =>
  value ? new Date(value).toLocaleDateString() : '—';

const statusClass = status =>
  ({
    paid: 'bg-n-teal-3 text-n-teal-11',
    pending: 'bg-n-amber-3 text-n-amber-11',
    overdue: 'bg-n-ruby-3 text-n-ruby-11',
    cancelled: 'bg-n-slate-3 text-n-slate-11',
  })[status] || 'bg-n-slate-3 text-n-slate-11';

onMounted(() => store.dispatch('reseller/getWholesale'));
</script>

<template>
  <div class="flex flex-col w-full max-w-3xl gap-4 py-6">
    <div>
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ t('REVENDA.WHOLESALE.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('REVENDA.WHOLESALE.SUBTITLE') }}
      </p>
    </div>

    <!-- Atacado não habilitado para este parceiro -->
    <div
      v-if="!isEnabled"
      class="flex flex-col items-center gap-2 p-8 text-center border rounded-xl border-n-weak"
    >
      <span class="text-2xl i-lucide-package text-n-slate-10" />
      <p class="text-sm text-n-slate-11">
        {{ t('REVENDA.WHOLESALE.DISABLED') }}
      </p>
    </div>

    <template v-else>
      <div class="grid grid-cols-2 gap-3 sm:max-w-md">
        <div
          class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <div class="flex items-center gap-2 text-n-slate-11">
            <span class="i-lucide-tag text-base" />
            <span class="text-xs">{{
              t('REVENDA.WHOLESALE.RETAIL_TOTAL')
            }}</span>
          </div>
          <span class="text-xl font-semibold text-n-slate-12 tabular-nums">
            {{ formatMoney(current.retail_total) }}
          </span>
        </div>
        <div
          class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <div class="flex items-center gap-2 text-n-slate-11">
            <span class="i-lucide-package text-base" />
            <span class="text-xs">{{
              t('REVENDA.WHOLESALE.WHOLESALE_TOTAL')
            }}</span>
          </div>
          <span class="text-xl font-semibold text-n-slate-12 tabular-nums">
            {{ formatMoney(current.wholesale_total) }}
          </span>
        </div>
      </div>

      <div
        v-if="invoices.length"
        class="overflow-x-auto border rounded-xl border-n-weak"
      >
        <table class="w-full min-w-full text-sm text-left">
          <thead class="text-n-slate-11 bg-n-solid-2">
            <tr>
              <th class="px-4 py-2 font-medium">
                {{ t('REVENDA.WHOLESALE.PERIOD') }}
              </th>
              <th class="px-4 py-2 font-medium">
                {{ t('REVENDA.WHOLESALE.AMOUNT') }}
              </th>
              <th class="px-4 py-2 font-medium">
                {{ t('REVENDA.WHOLESALE.STATUS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="inv in invoices" :key="inv.id">
              <td class="px-4 py-3 text-n-slate-11">
                {{ formatDate(inv.billing_period_start) }}
              </td>
              <td class="px-4 py-3 font-medium text-n-slate-12 tabular-nums">
                {{ formatMoney(inv.total_amount) }}
              </td>
              <td class="px-4 py-3">
                <span
                  class="inline-flex items-center px-2 py-0.5 text-xs rounded-full"
                  :class="statusClass(inv.status)"
                >
                  {{ inv.status }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-else
        class="flex flex-col items-center gap-2 p-8 text-center border rounded-xl border-n-weak"
      >
        <span class="text-2xl i-lucide-receipt text-n-slate-10" />
        <p class="text-sm text-n-slate-11">
          {{ t('REVENDA.WHOLESALE.NO_INVOICES') }}
        </p>
      </div>
    </template>
  </div>
</template>
